import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Params } from '@angular/router';
import { PopupService } from '@app/core/services/popup.service';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { BodyComponent } from '@shared/components/body/body.component';
import { BoxComponent } from '@shared/components/box/box.component';
import { DataFieldComponent } from '@shared/components/data-field/data-field.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { AutofillComponent } from '@shared/components/form/autofill/autofill.component';
import { LatLonInputComponent } from '@shared/components/form/lat-lon-input/lat-lon-input.component';
import { SelectDateComponent } from '@shared/components/form/select-date/select-date.component';
import { SelectComponent } from '@shared/components/form/select/select.component';
import { TextboxComponent } from '@shared/components/form/textbox/textbox.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { MovementSelectionComponent } from '@shared/components/movement-selection/movement-selection.component';
import { BodyBlockComponent } from '@shared/components/pdf-viewer/body-block/body-block.component';
import { PdfSubtitleBlockComponent } from '@shared/components/pdf-viewer/subtitle-block/subtitle-block.component';
import { SelectionButtonComponent } from '@shared/components/selection-button/selection-button.component';
import { SubtitleBlockComponent } from '@shared/components/subtitle-block/subtitle-block.component';
import { VesselItinerary } from '@shared/types/shared.types';
import { decimalToDDM } from '@shared/utils/helper';
import { ItineraryState } from '@vessel-itinerary/state/itinerary.state';
import { InputInventoryRobComponent } from '@vessel-reports/noon/components/input-inventory-rob/input-inventory-rob.component';
import { StepProgress, StepProgressComponent } from '@vessel-reports/noon/components/step-progress/step-progress.component';
import { SupplyDetailsComponent } from '@vessel-reports/noon/components/supply-details/supply-details.component';
import {
  Consumptions,
  PortInformationPort,
  PortInformationSea,
  PortSteps,
  REPORT_STATUS,
  REPORT_TYPE,
  SeaSteps,
  SwellDirections,
  VesselStatus,
  VesselTypes,
  Weather,
  WindDirections
} from '@vessel-reports/noon/noon.constants';
import { NoonActions } from '@vessel-reports/noon/state/noon.actions';
import { NoonState } from '@vessel-reports/noon/state/noon.state';
import {
  Bunker,
  Button,
  InputROB,
  NoonReportDetail,
  NoonReportSectionNumber,
  Quantity,
  ReportItem,
  Step,
  SwellDirection,
  WindDirection
} from '@vessel-reports/noon/state/noon.types';
import { VesselReportsAction } from '@vessel-reports/state/vessel-reports.action';
import { VesselReportsState } from '@vessel-reports/state/vessel-reports.state';
import { Movement, MovementDetail } from '@vessel-reports/state/vessel-reports.types';
import { DatetimeLocalPipe } from 'app/core/pipe/datetime/datetime-local.pipe';
import { MathRoundPipe } from 'app/core/pipe/math-round.pipe';
import { generateRangeObjects } from 'app/core/utils/helpers';
import dayjs from 'dayjs';
import { find, isEmpty } from 'lodash';
import { map } from 'lodash-es';
import cloneDeep from 'lodash-es/cloneDeep';
import { Subject, take, takeUntil } from 'rxjs';

@Component({
  selector: 'noon-report-details',
  imports: [
    TranslocoPipe,
    BoxComponent,
    HeaderComponent,
    BodyComponent,
    StepProgressComponent,
    SubtitleBlockComponent,
    MatIcon,
    TextboxComponent,
    AutofillComponent,
    SelectDateComponent,
    FooterComponent,
    SelectionButtonComponent,
    SelectComponent,
    PdfSubtitleBlockComponent,
    BodyBlockComponent,
    DataFieldComponent,
    InputInventoryRobComponent,
    SupplyDetailsComponent,
    MathRoundPipe,
    NgClass,
    DatetimeLocalPipe,
    LatLonInputComponent,
    MovementSelectionComponent
  ],
  templateUrl: './noon-report-details.component.html',
  styleUrl: './noon-report-details.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatetimeLocalPipe]
})
export class NoonReportDetailsComponent implements OnInit, OnDestroy {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);
  protected readonly datetimeLocalPipe: DatetimeLocalPipe = inject(DatetimeLocalPipe);
  protected readonly popupService: PopupService = inject(PopupService);
  protected readonly profileState: ProfileState = inject(ProfileState);
  seaSteps: WritableSignal<StepProgress[]> = signal(cloneDeep(SeaSteps));
  portSteps: WritableSignal<StepProgress[]> = signal(cloneDeep(PortSteps));
  buttons: WritableSignal<Button[]> = signal(cloneDeep(VesselTypes));
  protected readonly WindDirections: WindDirection[] = WindDirections;
  protected readonly SwellDirections: SwellDirection[] = SwellDirections;
  protected readonly Quantities: Quantity[] = generateRangeObjects(1, 3);
  protected readonly WaveDSS: Quantity[] = generateRangeObjects(1, 10);
  protected readonly WindBFScales: Quantity[] = generateRangeObjects(1, 12);
  protected readonly REPORT_STATUS: typeof REPORT_STATUS = REPORT_STATUS;
  protected readonly isEmpty: any = isEmpty;
  type: WritableSignal<'sea' | 'port'> = signal('sea');
  mode: WritableSignal<'input-rob' | 'input-inventory' | 'details' | 'last-port-movements' | 'next-port-movements' | 'current-port-movements'> =
    signal('details');
  currentSeaStep: WritableSignal<Step> = signal(1); // 5: review
  currentPortStep: WritableSignal<Step> = signal(1); // 3: review
  form: UntypedFormGroup;
  portInformationForm: UntypedFormGroup;
  vesselStatsForm: UntypedFormGroup;
  consumptionForm: UntypedFormGroup;
  weatherForm: UntypedFormGroup;
  detailsForm: UntypedFormGroup;
  detailNoonReport: Signal<NoonReportDetail> = computed(() => this._noonReportState.detailNoonReport());
  previousNoonReport: Signal<NoonReportDetail> = computed(() => this._noonReportState.previousReport());
  vesselMovements: WritableSignal<VesselItinerary[]> = signal([]);
  movements: Signal<Movement> = computed(() => this._vesselNoonReportState.pagination().movements);
  detailBunker: Signal<Bunker> = computed(() => this._noonReportState.detailBunker());
  private readonly _noonReportState: NoonState = inject(NoonState);
  private readonly _itineraryState: ItineraryState = inject(ItineraryState);
  private readonly _formBuilder: FormBuilder = inject(FormBuilder);
  private readonly _noonReportActions: NoonActions = inject(NoonActions);
  private readonly _vesselReportActions: VesselReportsAction = inject(VesselReportsAction);
  private readonly _vesselNoonReportState: VesselReportsState = inject(VesselReportsState);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _injector: Injector = inject(Injector);
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly decimalToDDM: any = decimalToDDM;
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close: EventEmitter<any> = new EventEmitter<any>();
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  queryParams: WritableSignal<Params> = signal(void 0);

  constructor() {
    this.useEffect();
  }

  ngOnInit() {
    try {
      this.type.set(this.detailNoonReport()?.report_type_id === REPORT_TYPE.SEA ? 'sea' : 'port');
      if (this.detailNoonReport() && this.detailNoonReport()?.bunker_id) {
        this._noonReportActions.actions.getBunker({
          bunker_id: this.detailNoonReport()?.bunker_id
        });
      }
      this.form = this._formBuilder.group({
        id: [this.detailNoonReport()?.bunker_id],
        rob_hfo: ['', Validators.required],
        rob_mgo: ['', Validators.required],
        rob_freshwater: ['', Validators.required],
        rob_luboil_me_cyl: ['', Validators.required],
        rob_luboil_me_sys: ['', Validators.required],
        rob_luboil_ge_sys: ['', Validators.required],
        report_type_id: [this.detailNoonReport()?.report_type_id, Validators.required],
        date_report: [this.detailNoonReport()?.date_report],
        voyage_number: [this.detailNoonReport()?.voyage_number],
        status_id: [this.detailNoonReport()?.status_id],
        details: this._formBuilder.array([], [Validators.required])
      });
      const portInformation = this.type() === 'sea' ? PortInformationSea : PortInformationPort;
      this.portInformationForm = this.createDetailsFormGroup(portInformation);
      this.vesselStatsForm = this.createDetailsFormGroup(VesselStatus);
      this.consumptionForm = this.createDetailsFormGroup(Consumptions);
      this.weatherForm = this.createDetailsFormGroup(Weather);
      this.detailsForm = this._formBuilder.group({
        vessel_report_id: [this.detailNoonReport()?.id],
        details: this._formBuilder.array([])
      });

      // Update step
      if (this.detailNoonReport()?.status_id === REPORT_STATUS.SUBMITTED || this.detailNoonReport()?.status_id === REPORT_STATUS.SAVED) {
        if (this.detailNoonReport()?.report_type_id === REPORT_TYPE.SEA) {
          this.currentSeaStep.set(5);
        } else {
          this.currentPortStep.set(3);
        }
      }
      this._activatedRoute.queryParams.pipe(takeUntil(this._unsubscribeAll)).subscribe((queryParams) => {
        this.queryParams.set({ ...queryParams });
      });
      this.updateDetailValues(this.portInformationForm, this.detailNoonReport()?.details);
      this.updateDetailValues(this.vesselStatsForm, this.detailNoonReport()?.details);
      this.updateDetailValues(this.consumptionForm, this.detailNoonReport()?.details);
      this.updateDetailValues(this.weatherForm, this.detailNoonReport()?.details);
      this.updatePreviousValues();
      this.updateButtonStatus();
      this.updateStepProgress();
      this.updateDisableFields();
    } catch (error) {
      console.log('error', error);
    }
  }

  get isDifferentType() {
    return (
      (this.detailNoonReport()?.report_type_id === REPORT_TYPE.SEA && this.previousNoonReport()?.report_type_id === REPORT_TYPE.PORT) ||
      (this.detailNoonReport()?.report_type_id === REPORT_TYPE.PORT && this.previousNoonReport()?.report_type_id === REPORT_TYPE.SEA)
    );
  }

  useEffect() {
    effect(
      () => {
        if (this.detailBunker()) {
          this.form.get('rob_hfo').setValue(this.detailBunker().rob_hfo);
          this.form.get('rob_mgo').setValue(this.detailBunker().rob_mgo);
          this.form.get('rob_freshwater').setValue(this.detailBunker().rob_freshwater);
          this.form.get('rob_luboil_me_cyl').setValue(this.detailBunker().rob_luboil_me_cyl);
          this.form.get('rob_luboil_me_sys').setValue(this.detailBunker().rob_luboil_me_sys);
          this.form.get('rob_luboil_ge_sys').setValue(this.detailBunker().rob_luboil_ge_sys);
        }
      },
      { injector: this._injector }
    );
  }

  updateDisableFields(): void {
    if (this.isFirstReport && !this.hasROB) {
      this.consumptionForm.get('details').disable();
    } else {
      this.consumptionForm.get('details').enable();
    }
  }

  createDetailsFormGroup(data: ReportItem[]): FormGroup {
    return this._formBuilder.group({
      details: this._formBuilder.array(
        data.map((info) =>
          this._formBuilder.group({
            id: [info.id],
            report_item_id: [info.report_item_id],
            value: [info.value, Validators.required]
          })
        )
      )
    });
  }

  updateButtonStatus() {
    if (!isEmpty(this.previousNoonReport())) {
      const newButtons = map(this.buttons(), (button: Button) => {
        if (button.value === this.getPreviousValueByReportTypeId(9)) {
          button.active = true;
        }
        return button;
      });
      this.buttons.set([...newButtons]);
    }
    if (this.detailNoonReport()) {
      const newButtons = map(this.buttons(), (button: Button) => {
        if (button.value === this.getDetailValueByReportTypeId(9)) {
          button.active = true;
        }
        return button;
      });
      this.buttons.set([...newButtons]);
    }
  }

  updatePreviousValues() {
    if (this.detailNoonReport()?.report_type_id === REPORT_TYPE.PORT) {
      !this.getControlByReportTypeId(this.portInformationForm, 38).value &&
        this.getControlByReportTypeId(this.portInformationForm, 38).setValue(this.movements()?.current_movement_id);
      !this.getControlByReportTypeId(this.portInformationForm, 1).value &&
        this.getControlByReportTypeId(this.portInformationForm, 1).setValue(this.movements()?.next_movement_id);
      !this.getControlByReportTypeId(this.portInformationForm, 39).value &&
        this.getControlByReportTypeId(this.portInformationForm, 39).setValue(this.movements()?.current_datetime_departure);
      !this.getControlByReportTypeId(this.portInformationForm, 6).value &&
        this.getControlByReportTypeId(this.portInformationForm, 6).setValue(this.movements()?.next_datetime_arrival);
    } else {
      !this.getControlByReportTypeId(this.vesselStatsForm, 9).value &&
        this.getControlByReportTypeId(this.vesselStatsForm, 9).setValue(this.getPreviousValueByReportTypeId(9));
      !this.getControlByReportTypeId(this.portInformationForm, 2).value &&
        this.getControlByReportTypeId(this.portInformationForm, 2).setValue(this.movements()?.previous_movement_id);
      !this.getControlByReportTypeId(this.portInformationForm, 1).value &&
        this.getControlByReportTypeId(this.portInformationForm, 1).setValue(this.movements()?.current_movement_id);
      !this.getControlByReportTypeId(this.portInformationForm, 6).value &&
        this.getControlByReportTypeId(this.portInformationForm, 6).setValue(this.movements()?.current_datetime_arrival);
    }
  }

  updateDetailValues(form: FormGroup, data: any[]) {
    const array = form.get('details') as FormArray;

    data?.forEach((newItem) => {
      const match = array.controls.find((ctrl) => ctrl.get('report_item_id')?.value === newItem.report_item_id);
      if (match) {
        if (
          newItem.report_item_id == 38 ||
          newItem.report_item_id == 18 ||
          newItem.report_item_id == 33 ||
          newItem.report_item_id == 35 ||
          newItem.report_item_id == 39 ||
          newItem.report_item_id == 6
        ) {
          return match.patchValue({ value: newItem.value ? Number(newItem.value) : '', id: newItem.id });
        }
        match.patchValue({ value: newItem.value, id: newItem.id });
      }
    });
  }

  isSubmitStatus() {
    return this.detailNoonReport()?.status_id === REPORT_STATUS.SUBMITTED;
  }

  isDisableSaveButton() {
    if (this.detailNoonReport()?.report_type_id === REPORT_TYPE.SEA) {
      return this.currentSeaStep() === 1
        ? this.portInformationForm.invalid
        : this.currentSeaStep() === 2
          ? this.vesselStatsForm.invalid
          : this.currentSeaStep() === 3
            ? this.consumptionForm.invalid
            : this.currentSeaStep() === 4
              ? this.weatherForm.invalid
              : false;
    }
    return this.currentPortStep() === 1 ? this.portInformationForm.invalid : this.currentPortStep() === 2 ? this.consumptionForm.invalid : false;
  }

  ngOnDestroy(): void {
    this._noonReportActions.actions.cleanState();
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  updateStepProgress() {
    if (this.type() === 'sea' && this.currentSeaStep() !== 5) {
      const steps = this.seaSteps();
      steps.forEach((step) => {
        step.active = false;
      });
      steps[this.currentSeaStep() - 1].active = true;
      this.seaSteps.set([...steps]);
    }
    if (this.type() === 'port' && this.currentPortStep() !== 3) {
      const steps = this.portSteps();
      steps.forEach((step) => {
        step.active = false;
      });
      steps[this.currentPortStep() - 1].active = true;
      this.portSteps.set([...steps]);
    }
  }

  get title() {
    if (isEmpty(this.detailNoonReport())) {
      return '';
    }
    let title = '';
    title =
      dayjs(this.detailNoonReport()?.date_report * 1000).format('DD MMM YYYY') +
      ' Noon Report ' +
      `( ${this.detailNoonReport()?.report_type_id === REPORT_TYPE.SEA ? 'SEA' : 'PORT'} )`;
    return title;
  }

  get sizeBox() {
    if ((this.type() === 'sea' && this.currentSeaStep() !== 5) || (this.type() === 'port' && this.currentPortStep() !== 3)) {
      return 'md';
    }
    return 'lg';
  }

  get saveTitle(): string {
    if (
      (this.detailNoonReport().status_id === REPORT_STATUS.SAVED && this.currentSeaStep() !== 5 && this.type() === 'sea') ||
      (this.detailNoonReport().status_id === REPORT_STATUS.SAVED && this.currentPortStep() !== 3 && this.type() === 'port')
    ) {
      return 'Save';
    }

    let title = 'Next';
    if (this.type() === 'sea') {
      if (this.currentSeaStep() === 4) {
        title = 'Save & Review';
      } else if (this.currentSeaStep() === 5) {
        title = 'Submit';
      }
    } else {
      if (this.currentPortStep() === 2) {
        title = 'Save & Review';
      } else if (this.currentPortStep() === 3) {
        title = 'Submit';
      }
    }

    if (this.detailNoonReport()?.status_id === REPORT_STATUS.SUBMITTED) {
      return 'Revert Status to Saved';
    }
    return title;
  }

  get hasROB() {
    return (
      this.form.get('rob_hfo').value ||
      this.form.get('rob_mgo').value ||
      this.form.get('rob_freshwater').value ||
      this.form.get('rob_luboil_me_cyl').value ||
      this.form.get('rob_luboil_me_sys').value ||
      this.form.get('rob_luboil_ge_sys').value
    );
  }

  get isFirstReport() {
    return isEmpty(this.previousNoonReport());
  }

  getControlByReportTypeId(control: UntypedFormGroup, reportTypeId: number): FormControl {
    const array = control.get('details') as FormArray;

    const group = array.controls.find((g) => g.get('report_item_id')?.value === reportTypeId) as FormGroup;

    return group ? (group.get('value') as FormControl) : null;
  }

  getPreviousValueByReportTypeId(reportTypeId: number, valueMovementType?: string): any {
    try {
      if (!this.previousNoonReport()) {
        return '';
      }
      const reportItem = this.previousNoonReport()?.details?.find((d) => d?.report_item_id === reportTypeId);

      if (reportTypeId === 1 || reportTypeId === 2 || reportTypeId === 38) {
        return find(this?.vesselMovements(), (movementDetail: MovementDetail) => movementDetail?.id == reportItem?.value)?.[valueMovementType || 'port_name'];
      }
      if (reportTypeId == 18 || reportTypeId == 33 || reportTypeId == 35 || reportTypeId == 39 || reportTypeId == 6 || reportTypeId == 9) {
        return reportItem?.value ? Number(reportItem?.value) : '';
      }
      return reportItem?.value || '';
    } catch (e) {
      console.log('getPreviousValueByReportTypeId e', e);
    }
  }

  getDetailValueByReportTypeId(reportTypeId: number, valueMovementType?: string): any {
    if (!this.detailNoonReport()) {
      return '';
    }

    const reportItem = this.detailNoonReport()?.details?.find((d) => d?.report_item_id === reportTypeId);
    if (reportTypeId === 1 || reportTypeId === 2 || reportTypeId === 38) {
      return find(this.vesselMovements(), (movementDetail: MovementDetail) => movementDetail?.id == reportItem?.value)?.[valueMovementType || 'port_name'];
    }
    if (reportTypeId == 18 || reportTypeId == 33 || reportTypeId == 35 || reportTypeId == 39 || reportTypeId == 6 || reportTypeId == 9) {
      return reportItem?.value ? Number(reportItem?.value) : '';
    }
    return reportItem?.value || '';
  }

  getSupplyById(id: number) {
    if (!this.detailNoonReport()) {
      return '';
    }
    const supplyItem = this.detailNoonReport()?.supplies?.find((d) => d?.inventory_item_id === id);
    return supplyItem?.quantity || '';
  }

  convertFormToFormData(form: FormGroup): FormData {
    const formData = new FormData();
    formData.append('vessel_report_id', form.get('vessel_report_id')?.value);

    const detailsArray = form.get('details') as FormArray;

    detailsArray.controls.forEach((group: AbstractControl, index: number) => {
      const groupValue = group.value;
      for (const key in groupValue) {
        if (Object.prototype.hasOwnProperty.call(groupValue, key)) {
          groupValue[key] && formData.append(`details[${index}][${key}]`, groupValue[key]);
        }
      }
    });

    return formData;
  }

  isEnableCopy() {
    return !isEmpty(this.previousNoonReport());
  }

  onDiscard: FunctionType = () => {
    this.onClose();
  };

  onSave: FunctionType = () => {
    try {
      if (this.detailNoonReport().status_id === 3) {
        this._noonReportActions.actions.updateReport({
          report_id: this.detailNoonReport().id,
          body: {
            status_id: 2,
            date_report: this.detailNoonReport().date_report,
            voyage_number: this.detailNoonReport().voyage_number
          }
        });
        this._noonReportActions.actions.updateReportSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this._noonReportActions.actions.getDetailNoonReport({ report_id: this.detailNoonReport()?.id });
          this._noonReportActions.actions.getDetailNoonReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this._noonReportActions.actions.getBunker({
              bunker_id: this.detailNoonReport()?.bunker_id
            });
            this.getVesselsReport('1,2', this.queryParams());
          });
        });
        return;
      }
      if (this.type() === 'sea') {
        const form =
          this.currentSeaStep() === 1
            ? this.portInformationForm
            : this.currentSeaStep() === 2
              ? this.vesselStatsForm
              : this.currentSeaStep() === 3
                ? this.consumptionForm
                : this.weatherForm;
        const status = this.currentSeaStep() === 1 ? REPORT_STATUS.DRAFT : this.currentSeaStep() === 5 ? REPORT_STATUS.SUBMITTED : REPORT_STATUS.SAVED;
        this.handleUpdateDetailsReport(form, status, () => {
          if (this.detailNoonReport().status_id === REPORT_STATUS.SAVED && this.currentSeaStep() !== 5) {
            return this.currentSeaStep.set(5);
          }
          if (this.currentSeaStep() === 1) {
            this.currentSeaStep.set(2);
          } else if (this.currentSeaStep() === 2) {
            this.currentSeaStep.set(3);
          } else if (this.currentSeaStep() === 3) {
            this.currentSeaStep.set(4);
          } else if (this.currentSeaStep() === 4) {
            this.currentSeaStep.set(5);
          } else {
            this.onClose();
          }
          this.updateStepProgress();
        });
      } else {
        const form = this.currentPortStep() === 1 ? this.portInformationForm : this.consumptionForm;
        const status = this.currentPortStep() === 1 ? REPORT_STATUS.DRAFT : this.currentPortStep() === 2 ? REPORT_STATUS.SAVED : REPORT_STATUS.SUBMITTED;
        this.handleUpdateDetailsReport(form, status, () => {
          if (this.detailNoonReport().status_id === REPORT_STATUS.SAVED && this.currentPortStep() !== 3) {
            return this.currentPortStep.set(3);
          }
          if (this.currentPortStep() === 1) {
            this.currentPortStep.set(2);
          } else if (this.currentPortStep() === 2) {
            this.currentPortStep.set(3);
          } else {
            this.onClose();
          }
          this.updateStepProgress();
        });
      }
    } catch (e) {
      console.log('error', e);
    }
  };

  handleUpdateDetailsReport(form: any, status: REPORT_STATUS, cb?: () => void) {
    if (status === REPORT_STATUS.SUBMITTED) {
      const eta: string = this.datetimeLocalPipe.transform(this.getDetailValueByReportTypeId(6), { format: 'DD MMM YYYY HH:mm' });
      const etd: string = this.datetimeLocalPipe.transform(this.getDetailValueByReportTypeId(39), { format: 'DD MMM YYYY HH:mm' });
      const content =
        this.type() === 'sea'
          ? `<div>The following data has been updated to vessel movement:</div>
            <div>ETA: ${eta}</div>`
          : `<div>The following data has been updated to vessel movement:</div>
            <div>ETD: ${etd}</div>`;
      this.popupService.openConfirmationDialog({
        title: 'Updated vessel itinerary',
        message: content,
        iconType: 'success',
        onCancel: () => {},
        onAccept: () => {
          this._noonReportActions.actions.submitNoonReport({ report_id: this.detailNoonReport()?.id });
          this._noonReportActions.actions.submitNoonReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.getVesselsReport('1,2', this.queryParams());
            cb?.();
          });
        }
      });
    } else {
      this.detailsForm.get('details').reset();
      const formArray = this.detailsForm.get('details') as FormArray;
      const details = form?.get('details').value;
      details?.forEach((item) => {
        formArray.push(
          this._formBuilder.group({
            id: [item.id],
            report_item_id: [item.report_item_id],
            value: [item.report_item_id === 9 ? item.value.toString() : item.value]
          })
        );
      });
      const formData = this.convertFormToFormData(this.detailsForm);
      if (this.currentPortStep() == 2 || this.currentSeaStep() === 4) {
        formData.append('status_id', String(REPORT_STATUS.SAVED));
      }
      this._noonReportActions.actions.updateDetailNoonReport({ body: formData });
      this._noonReportActions.actions.updateDetailNoonReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
        if (this.currentPortStep() == 2 || this.currentSeaStep() === 4) {
          this.getVesselsReport('1,2', this.queryParams());
        }
        this.getDetailReportWithCallback(cb);
      });
    }
  }

  onClose: FunctionType = () => {
    this.close.emit();
  };

  onPressSection(step: Step) {
    if (this.type() === 'sea') {
      this.currentSeaStep.set(step);
    } else {
      this.currentPortStep.set(step);
    }
    this.updateStepProgress();
  }

  onPressInventorySection(step: Step) {
    if (this.type() === 'sea') {
      this.currentSeaStep.set(step);
    } else {
      this.currentPortStep.set(step);
    }
    this.updateStepProgress();
    this.mode.set('input-inventory');
  }

  onClickROB() {
    this.mode.set('input-rob');
  }

  onBack() {
    this.mode.set('details');
  }

  onSaveROB(rob: InputROB) {
    this.form.patchValue(rob);
    this.consumptionForm.get('details').enable();
    this.mode.set('details');
  }

  onSaveSupplyDetails(rob: InputROB) {
    console.log('onSaveSupplyDetails', rob);
  }

  onPressCopy(number: NoonReportSectionNumber) {
    if (this.type() === 'sea') {
      switch (number) {
        case 1: {
          this.copyPrevValueReportTypeId(this.portInformationForm, 2);
          this.copyPrevValueReportTypeId(this.portInformationForm, 4);
          this.copyPrevValueReportTypeId(this.portInformationForm, 5);
          break;
        }
        case 2: {
          this.copyPrevValueReportTypeId(this.portInformationForm, 1);
          this.copyPrevValueReportTypeId(this.portInformationForm, 3);
          this.copyPrevValueReportTypeId(this.portInformationForm, 6);
          break;
        }
        case 3: {
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 7);
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 8);
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 9);
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 14);
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 10);
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 11);
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 12);
          break;
        }
        case 4: {
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 13);
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 16);
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 17);
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 15);
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 18);
          this.copyPrevValueReportTypeId(this.vesselStatsForm, 19);
          break;
        }
        case 5: {
          this.copyPrevValueReportTypeId(this.consumptionForm, 20);
          this.copyPrevValueReportTypeId(this.consumptionForm, 21);
          this.copyPrevValueReportTypeId(this.consumptionForm, 22);
          break;
        }
        case 6: {
          this.copyPrevValueReportTypeId(this.consumptionForm, 23);
          this.copyPrevValueReportTypeId(this.consumptionForm, 24);
          this.copyPrevValueReportTypeId(this.consumptionForm, 25);
          break;
        }
        case 7: {
          this.copyPrevValueReportTypeId(this.consumptionForm, 26);
          this.copyPrevValueReportTypeId(this.consumptionForm, 27);
          this.copyPrevValueReportTypeId(this.consumptionForm, 28);
          break;
        }
        case 8: {
          this.copyPrevValueReportTypeId(this.consumptionForm, 29);
          this.copyPrevValueReportTypeId(this.consumptionForm, 30);
          break;
        }
        case 9: {
          this.copyPrevValueReportTypeId(this.weatherForm, 31);
          this.copyPrevValueReportTypeId(this.weatherForm, 32);
          this.copyPrevValueReportTypeId(this.weatherForm, 33);
          break;
        }
        case 10: {
          this.copyPrevValueReportTypeId(this.weatherForm, 34);
          this.copyPrevValueReportTypeId(this.weatherForm, 35);
          this.copyPrevValueReportTypeId(this.weatherForm, 36);
          this.copyPrevValueReportTypeId(this.weatherForm, 37);
          break;
        }
      }
    } else {
      switch (number) {
        case 1: {
          this.copyPrevValueReportTypeId(this.portInformationForm, 38);
          this.copyPrevValueReportTypeId(this.portInformationForm, 39);
          break;
        }
        case 2: {
          this.copyPrevValueReportTypeId(this.portInformationForm, 1);
          this.copyPrevValueReportTypeId(this.portInformationForm, 6);
          break;
        }
        case 3: {
          this.copyPrevValueReportTypeId(this.consumptionForm, 20);
          this.copyPrevValueReportTypeId(this.consumptionForm, 21);
          this.copyPrevValueReportTypeId(this.consumptionForm, 22);
          break;
        }
        case 4: {
          this.copyPrevValueReportTypeId(this.consumptionForm, 23);
          this.copyPrevValueReportTypeId(this.consumptionForm, 24);
          this.copyPrevValueReportTypeId(this.consumptionForm, 25);
          break;
        }
        case 5: {
          this.copyPrevValueReportTypeId(this.consumptionForm, 26);
          this.copyPrevValueReportTypeId(this.consumptionForm, 27);
          this.copyPrevValueReportTypeId(this.consumptionForm, 28);
          break;
        }
        case 6: {
          this.copyPrevValueReportTypeId(this.consumptionForm, 29);
          this.copyPrevValueReportTypeId(this.consumptionForm, 30);
          break;
        }
      }
    }
  }

  copyPrevValueReportTypeId(control: FormGroup, reportTypeId: number) {
    const prevValue = this.getPreviousValueByReportTypeId(reportTypeId, 'id');
    this.getControlByReportTypeId(control, reportTypeId).setValue(prevValue);
  }

  onPressLastPort() {
    this.mode.set('last-port-movements');
  }

  onPressNextPort() {
    this.mode.set('next-port-movements');
  }

  onPressCurrentPort() {
    this.mode.set('current-port-movements');
  }

  onSavePort(movement: Partial<VesselItinerary>, type?: number) {
    if (type === 38) {
      this.getControlByReportTypeId(this.portInformationForm, 39).setValue(movement?.datetime_departure);
    }
    if (type === 1) {
      this.getControlByReportTypeId(this.portInformationForm, 6).setValue(movement?.datetime_arrival);
    }
    this.getControlByReportTypeId(this.portInformationForm, type).setValue(movement?.id);
    this.mode.set('details');
  }

  onButtonsChange(buttons: Button[]) {
    const activeButton = buttons.find((button) => button.active);
    if (activeButton) {
      this.getControlByReportTypeId(this.vesselStatsForm, 9).setValue(activeButton.value);
    }
  }

  onPressSupplyInventory() {
    this.mode.set('input-inventory');
  }

  getVesselsReport(type: string, queryParams: Params): void {
    this._vesselReportActions.actions.getVesselReports({
      type,
      queryParams
    });
  }

  getDetailReportWithCallback(cb?: () => void): void {
    this._noonReportActions.actions.getDetailNoonReport({ report_id: this.detailNoonReport()?.id });
    if (cb) {
      this._noonReportActions.actions.getDetailNoonReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
        cb();
      });
    }
  }
  appendVesselMovements(movements: VesselItinerary[]): void {
    const ids = movements.map((item) => item.id);
    this.vesselMovements.update((prev) => [...prev.filter((item) => !ids.includes(item.id)), ...movements]);
  }
}
